import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Card, CardContent } from './ui/card';
import { 
  MapPin, Calendar, Package, AlertCircle, Save, 
  Loader2, Info, Lock, Shield, Navigation, Plus, Minus, Route
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { getStatusLabel } from '../constants/statuses';
// Réutilisation des composants du formulaire de création
import AddressInput from './AddressInput';
import MultiStopInput from './MultiStopInput';
import VehicleSelector from './VehicleSelector';
import VehicleSearchInput from './VehicleSearchInput';
import RoundTripSection from './operation/RoundTripSection';
import { vehicleTypes, operationTypes, merchandiseTypes, containerTypes } from '../mockData';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuts qui permettent la modification par le client (phase initiale uniquement)
const MODIFIABLE_STATUSES_CLIENT = [
  'nouvelle',
  'demande_enregistree'
];

// Statuts qui permettent la modification par le staff (plus large)
const MODIFIABLE_STATUSES_STAFF = [
  'nouvelle',
  'demande_enregistree',
  'en_attente_de_devis',
  'en_attente_validation_client',
  'en_negociation',
  'devis_accepte',
  'planifiee',
  'en_cours'  // Staff peut modifier chauffeur uniquement (remplacement urgent)
];

const EditOperationModal = ({ 
  isOpen, 
  onClose, 
  operation, 
  onSuccess,
  userRole = 'client'
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showContainerOptions, setShowContainerOptions] = useState(false);
  const [showMultiStop, setShowMultiStop] = useState(false);
  
  // État pour le calcul de distance (comme dans TransportForm)
  const [distanceInfo, setDistanceInfo] = useState({
    distance_km: null,
    isCalculating: false,
    error: null
  });

  const isStaff = ['admin', 'operateur', 'commercial', 'field_ops'].includes(userRole);
  const isAdmin = userRole === 'admin';
  
  // Determine if modification is allowed based on status and role
  // Admin can always modify
  const modifiableStatuses = isAdmin ? null : (isStaff ? MODIFIABLE_STATUSES_STAFF : MODIFIABLE_STATUSES_CLIENT);
  const canModify = isAdmin || modifiableStatuses.includes(operation?.status);
  
  // Determine which fields can be modified based on status
  const getEditableFields = () => {
    // Admin can edit ALL fields at ANY status
    if (isAdmin) {
      return {
        trajet: true,
        dates: true,
        vehicule: true,
        marchandise: true,
        assurance: true,
        chauffeur: true
      };
    }
    
    const status = operation?.status;
    
    // Early stages - everything is modifiable (includes 'nouvelle')
    if (['nouvelle', 'demande_enregistree', 'en_attente_de_devis'].includes(status)) {
      return {
        trajet: true,
        dates: true,
        vehicule: true,
        marchandise: true,
        assurance: true,
        chauffeur: isStaff
      };
    }
    
    // After quote sent - limited modifications
    if (['en_attente_validation_client', 'en_negociation'].includes(status)) {
      return {
        trajet: isStaff, // Only staff can change route after quote,
        dates: true,
        vehicule: isStaff,
        marchandise: isStaff,
        assurance: true,
        chauffeur: isStaff
      };
    }
    
    // After quote accepted - very limited
    if (['devis_accepte', 'planifiee'].includes(status)) {
      return {
        trajet: false,
        dates: isStaff, // Only staff can change dates,
        vehicule: isStaff, // Staff can change vehicle/driver,
        marchandise: false,
        assurance: false,
        chauffeur: isStaff
      };
    }
    
    // En cours - only driver replacement (urgent)
    if (status === 'en_cours') {
      return {
        trajet: false,
        dates: false,
        vehicule: false,
        marchandise: false,
        assurance: false,
        chauffeur: isStaff  // Only staff can replace driver
      };
    }
    
    // Default - read only (terminee, etc.)
    return {
      trajet: false,
      dates: false,
      vehicule: false,
      marchandise: false,
      assurance: false,
      chauffeur: false
    };
  };

  const editableFields = getEditableFields();

  // PM Spec 27/04/2026 (Q1=b) — Le toggle Aller-Retour est modifiable uniquement
  // avant émission du devis. Au-delà, le bloc est visible en lecture seule.
  // Admin garde la possibilité de corriger en pré-devis comme tout le monde,
  // mais pas de bypass au-delà : la cohérence PDF prime.
  const AR_EDITABLE_STATUSES = ['nouvelle', 'demande_enregistree', 'en_attente_de_devis'];
  const canEditTripType = AR_EDITABLE_STATUSES.includes(operation?.status);

  // Bandeau d'info Q2=b — quand l'admin switche le tripType, on rappelle l'impact distance.
  const initialTripType = operation?.tripType || 'one_way';
  const tripTypeChanged = formData.tripType && formData.tripType !== initialTripType;

  // Fonction pour calculer la distance entre deux points (identique à TransportForm)
  const calculateDistance = useCallback(async (departure, arrival) => {
    if (!departure || !arrival || departure.length < 3 || arrival.length < 3) {
      setDistanceInfo({ distance_km: null, isCalculating: false, error: null });
      return;
    }

    setDistanceInfo(prev => ({ ...prev, isCalculating: true, error: null }));

    try {
      const response = await axios.get(`${API}/external/distance/estimate`, {
        params: { from_city: departure, to_city: arrival }
      });
      const km = response.data?.distance_km || null;
      setDistanceInfo({
        distance_km: km,
        isCalculating: false,
        error: null
      });
      // Propager dans formData pour que RoundTripSection affiche la bonne distance totale
      if (km !== null) {
        setFormData(prev => ({ ...prev, distance_km: km }));
      }
    } catch (error) {
      logger.debug('Distance calculation error:', error.message);
      setDistanceInfo({ distance_km: null, isCalculating: false, error: null });
    }
  }, []);

  // Debounced distance calculation when addresses change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.departure && formData.arrival) {
        calculateDistance(formData.departure, formData.arrival);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [formData.departure, formData.arrival, calculateDistance]);

  // Track the operation ID to only reinitialize when opening for a different operation
  const [initializedForOperationId, setInitializedForOperationId] = useState(null);

  // Initialize form data only when modal opens for a NEW operation
  // This prevents auto-refresh from resetting user's in-progress edits
  useEffect(() => {
    if (operation && isOpen && operation.id !== initializedForOperationId) {
      // Déterminer si c'est un transport de conteneur
      const opType = operationTypes.find(op => op.value === operation.operationType);
      setShowContainerOptions(opType?.showContainerOption || false);
      
      setFormData({
        departure: operation.departure || '',
        arrival: operation.arrival || '',
        departureCoordinates: operation.departureCoordinates || null,
        arrivalCoordinates: operation.arrivalCoordinates || null,
        deliveryDate: operation.deliveryDate ? operation.deliveryDate.split('T')[0] : '',
        vehicleType: operation.vehicleType || '',
        vehicleTypeOther: operation.vehicleTypeOther || '',
        operationType: operation.operationType || '',
        operationTypeOther: operation.operationTypeOther || '',
        merchandiseType: operation.merchandiseType || '',
        merchandiseTypeOther: operation.merchandiseTypeOther || '',
        containerType: operation.containerType || '',
        weight: operation.weight || 1,
        numberOfTrucks: operation.numberOfTrucks || 1,
        needsInsurance: operation.needsInsurance || false,
        merchandiseValue: operation.merchandiseValue || '',
        specialInstructions: operation.specialInstructions || '',
        stops: operation.stops || [],
        // Aller-retour (PM Spec 27/04/2026)
        tripType: operation.tripType || 'one_way',
        returnCargoMode: operation.returnCargoMode || null,
        returnMerchandiseType: operation.returnMerchandiseType || '',
        returnMerchandiseTypeOther: operation.returnMerchandiseTypeOther || '',
        returnWeight: operation.returnWeight || '',
        returnContainerType: operation.returnContainerType || '',
        returnDepartureDate: operation.returnDepartureDate || '',
        distance_km: operation.distance_km || null,
      });
      // Show multi-stop section if operation already has stops
      if ((operation.stops || []).length > 0) {
        setShowMultiStop(true);
      }
      setHasChanges(false);
      setInitializedForOperationId(operation.id);
      
      // Calculer la distance initiale si on a les adresses
      if (operation.departure && operation.arrival) {
        calculateDistance(operation.departure, operation.arrival);
      }
    }
  }, [operation, isOpen, initializedForOperationId, calculateDistance]);

  // Reset the initialized ID when modal closes to allow re-initialization on next open
  useEffect(() => {
    if (!isOpen) {
      setInitializedForOperationId(null);
    }
  }, [isOpen]);

  // Handle address changes with coordinates (from AddressInput)
  const handleAddressChange = (field, value, coordinates = null) => {
    if (coordinates && (field === 'departure' || field === 'arrival')) {
      const coordField = field === 'departure' ? 'departureCoordinates' : 'arrivalCoordinates';
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        [coordField]: coordinates
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    setHasChanges(true);
  };

  // Track changes
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    
    // Gérer l'affichage des options de conteneur
    if (field === 'operationType') {
      const selectedOp = operationTypes.find(op => op.value === value);
      setShowContainerOptions(selectedOp?.showContainerOption || false);
      
      if (selectedOp?.showContainerOption && !formData.containerType) {
        setFormData(prev => ({ ...prev, containerType: containerTypes[0]?.value || '' }));
      } else if (!selectedOp?.showContainerOption) {
        setFormData(prev => ({ ...prev, containerType: '' }));
      }
    }
  };

  // Weight handlers (like TransportForm)
  const handleWeightChange = (increment) => {
    const newWeight = Math.max(1, Math.min(220, (formData.weight || 1) + increment));
    handleChange('weight', newWeight);
  };

  const handleWeightInput = (value) => {
    if (value === '') {
      setFormData(prev => ({ ...prev, weight: '' }));
      setHasChanges(true);
      return;
    }
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue)) {
      handleChange('weight', Math.max(1, Math.min(220, numValue)));
    }
  };

  // Submit modifications
  const handleSubmit = async () => {
    if (!hasChanges) {
      toast({ title: 'Aucune modification', description: 'Aucun changement à enregistrer.' });
      return;
    }

    setLoading(true);
    try {
      // Prepare only modified fields
      const modifiedFields = {};
      Object.keys(formData).forEach(key => {
        const originalValue = operation[key];
        const newValue = formData[key];
        
        // Handle date comparison
        if (key === 'deliveryDate') {
          const originalDate = originalValue ? originalValue.split('T')[0] : '';
          if (originalDate !== newValue && newValue) {
            modifiedFields[key] = newValue;
          }
        } else if (key === 'stops') {
          // Always include stops if they changed (array comparison)
          if (JSON.stringify(originalValue || []) !== JSON.stringify(newValue || [])) {
            modifiedFields[key] = (newValue || []).map((stop, index) => ({
              order: index + 1,
              address: stop.address || '',
              city: stop.city || '',
              country: stop.country || 'CI',
              coordinates: stop.coordinates || null,
              instructions: stop.instructions || ''
            }));
          }
        } else if (originalValue !== newValue && newValue !== '') {
          modifiedFields[key] = newValue;
        }
      });

      if (Object.keys(modifiedFields).length === 0) {
        toast({ title: 'Aucune modification', description: 'Aucun changement détecté.' });
        setLoading(false);
        return;
      }

      const response = await axios.patch(
        `${API}/transport-requests/${operation.id}/client-update`,
        modifiedFields,
        { headers: { 'Authorization': 'Bearer cookie-auth' } }
      );

      if (response.data.success) {
        toast({
          title: 'Opération modifiée',
          description: `${response.data.modifiedFields?.length || 0} champ(s) mis à jour avec succès.`
        });
        onSuccess?.(response.data.operation);
        onClose();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Erreur lors de la modification';
      if (detail) {
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(e => e.msg || e.message).join(', ');
      }
      toast({ title: 'Erreur', description: errorMessage, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (!operation) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Modifier l'opération {operation.operationNumber}
          </DialogTitle>
          <DialogDescription>
            Modifiez les informations de l'opération. Certains champs peuvent être verrouillés selon le statut actuel.
          </DialogDescription>
        </DialogHeader>

        {/* Status Warning */}
        <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <Info className="w-5 h-5 text-blue-600 shrink-0" />
          <div>
            <span className="text-sm font-medium text-blue-900 flex items-center gap-2">
              Statut actuel : <Badge variant="outline">{getStatusLabel(operation.status)}</Badge>
            </span>
            {!canModify && (
              <p className="text-xs text-blue-700 mt-1">
                L'opération est trop avancée pour être modifiée. Contactez le support si nécessaire.
              </p>
            )}
          </div>
        </div>

        {canModify ? (
          <div className="space-y-6 py-4">
            {/* === TRAJET (identique à TransportForm) === */}
            <div className={`space-y-4 ${!editableFields.trajet ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[#1db584]" />
                <h3 className="font-semibold text-gray-900">Détails du trajet *</h3>
                {!editableFields.trajet && (
                  <Badge variant="secondary" className="text-xs ml-auto">
                    <Lock className="w-3 h-3 mr-1" />Verrouillé
                  </Badge>
                )}
              </div>

              <div>
                {editableFields.trajet ? (
                  <AddressInput
                    value={formData.departure || ''}
                    onChange={(value, coords) => handleAddressChange('departure', value, coords)}
                    placeholder="Adresse de départ"
                  />
                ) : (
                  <Input value={formData.departure || ''} disabled className="bg-gray-50" />
                )}
              </div>

              {/* Multi-Stop Toggle - Comme TransportForm (entre départ et arrivée) */}
              {editableFields.trajet && (
                <div className="border-t border-b border-dashed border-gray-200 py-4 my-2">
                  <button
                    type="button"
                    onClick={() => setShowMultiStop(!showMultiStop)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#1db584] transition-colors w-full justify-center"
                  >
                    <Route className="w-4 h-4" />
                    {showMultiStop ? 'Masquer les étapes intermédiaires' : 'Ajouter des étapes intermédiaires'}
                    {(formData.stops || []).length > 0 && (
                      <span className="ml-2 bg-[#1db584] text-white text-xs px-2 py-0.5 rounded-full">
                        {(formData.stops || []).length}
                      </span>
                    )}
                  </button>
                  
                  {showMultiStop && (
                    <div className="mt-4">
                      <MultiStopInput
                        stops={formData.stops || []}
                        onStopsChange={(stops) => {
                          setFormData(prev => ({ ...prev, stops }));
                          setHasChanges(true);
                        }}
                        maxStops={10}
                      />
                    </div>
                  )}
                </div>
              )}

              <div>
                {editableFields.trajet ? (
                  <AddressInput
                    value={formData.arrival || ''}
                    onChange={(value, coords) => handleAddressChange('arrival', value, coords)}
                    placeholder="Adresse d'arrivée"
                  />
                ) : (
                  <Input value={formData.arrival || ''} disabled className="bg-gray-50" />
                )}
              </div>

              {/* Distance Indicator */}
              {(distanceInfo.isCalculating || distanceInfo.distance_km) && (
                <div className="mt-4 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-200 animate-in fade-in duration-300">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-full shadow-sm">
                      <Navigation className="w-4 h-4 text-[#1db584]" />
                    </div>
                    <div className="flex-1">
                      {distanceInfo.isCalculating ? (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Calcul de la distance...</span>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold text-[#1db584]">
                              {distanceInfo.distance_km?.toLocaleString('fr-FR')}
                            </span>
                            <span className="text-sm text-gray-600">km</span>
                          </div>
                          <span className="text-xs text-gray-500">Distance estimée du trajet</span>
                          {(formData.stops || []).length > 0 && (
                            <div className="mt-1 flex items-center gap-1">
                              <Route className="w-3 h-3 text-[#1db584]" />
                              <span className="text-xs text-[#1db584] font-medium">
                                {(formData.stops || []).length} étape{(formData.stops || []).length > 1 ? 's' : ''} intermédiaire{(formData.stops || []).length > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <MapPin className="w-5 h-5 text-emerald-400" />
                  </div>
                </div>
              )}
            </div>

            {/* === ALLER-RETOUR (PM Spec 27/04/2026) === */}
            {/* Visible toujours, modifiable avant émission devis (Q1=b).
                Bandeau d'info quand le mode change (Q2=b). */}
            <div
              className={`space-y-3 rounded-lg p-3 border ${
                canEditTripType ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50 opacity-90'
              }`}
              data-testid="edit-modal-roundtrip-wrapper"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">Type de trajet</h3>
                {!canEditTripType && (
                  <Badge variant="secondary" className="text-xs">
                    <Lock className="w-3 h-3 mr-1" />
                    Lecture seule — Réviser le devis pour modifier
                  </Badge>
                )}
              </div>
              <div
                className={!canEditTripType ? 'pointer-events-none select-none' : ''}
                aria-disabled={!canEditTripType}
              >
                <RoundTripSection
                  formData={formData}
                  setFormData={(updater) => {
                    if (!canEditTripType) return;
                    setFormData(updater);
                    setHasChanges(true);
                  }}
                  merchandiseTypes={merchandiseTypes}
                  containerTypes={containerTypes}
                />
              </div>
              {tripTypeChanged && canEditTripType && formData.distance_km > 0 && (
                <div
                  data-testid="edit-modal-roundtrip-info-banner"
                  className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-2"
                >
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Distance totale&nbsp;:&nbsp;
                    <strong>
                      {Math.round(
                        formData.tripType === 'round_trip'
                          ? formData.distance_km * 2
                          : formData.distance_km
                      )}{' '}
                      km
                    </strong>{' '}
                    (au lieu de{' '}
                    {Math.round(
                      initialTripType === 'round_trip'
                        ? formData.distance_km * 2
                        : formData.distance_km
                    )}{' '}
                    km).{' '}
                    Le tarif client sera à recalculer dans le devis.
                  </span>
                </div>
              )}
            </div>

            {/* === DATE DE LIVRAISON === */}
            <div className={`space-y-2 ${!editableFields.dates ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#1db584]" />
                <Label>Date de livraison *</Label>
                {!editableFields.dates && (
                  <Badge variant="secondary" className="text-xs ml-auto">
                    <Lock className="w-3 h-3 mr-1" />Verrouillé
                  </Badge>
                )}
              </div>
              <Input
                type="date"
                value={formData.deliveryDate || ''}
                onChange={(e) => handleChange('deliveryDate', e.target.value)}
                disabled={!editableFields.dates}
                className="focus:border-[#1db584] focus:ring-[#1db584]"
              />
            </div>

            {/* === VÉHICULE === */}
            <div className={`space-y-3 ${!editableFields.vehicule ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">Choisir un véhicule *</h3>
                {!editableFields.vehicule && (
                  <Badge variant="secondary" className="text-xs ml-auto">
                    <Lock className="w-3 h-3 mr-1" />Verrouillé
                  </Badge>
                )}
              </div>
              {editableFields.vehicule ? (
                <>
                  <VehicleSelector
                    vehicles={vehicleTypes}
                    selected={formData.vehicleType}
                    onSelect={(value) => handleChange('vehicleType', value)}
                  />
                  {formData.vehicleType === 'autre' && (
                    <div className="mt-3 animate-in slide-in-from-top-2 duration-300">
                      <Label className="text-sm text-gray-600 mb-2 block">
                        Recherchez ou saisissez le type de véhicule
                      </Label>
                      <VehicleSearchInput
                        value={formData.vehicleTypeOther || ''}
                        onChange={(value) => handleChange('vehicleTypeOther', value)}
                        placeholder="Rechercher un type de véhicule..."
                      />
                    </div>
                  )}
                </>
              ) : (
                <Input 
                  value={vehicleTypes.find(v => v.id === formData.vehicleType)?.name || formData.vehicleType || ''} 
                  disabled 
                  className="bg-gray-50" 
                />
              )}
            </div>

            {/* === NATURE DE L'OPÉRATION === */}
            <div className={`space-y-2 ${!editableFields.vehicule ? 'opacity-60 pointer-events-none' : ''}`}>
              <Label>Nature de l&apos;opération *</Label>
              <Select
                value={formData.operationType || undefined}
                onValueChange={(value) => handleChange('operationType', value)}
                disabled={!editableFields.vehicule}
              >
                <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584]">
                  <SelectValue placeholder="-- Sélectionnez --" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] overflow-y-auto">
                  {operationTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.operationType === 'operation_speciale' && editableFields.vehicule && (
                <div className="mt-2 animate-in slide-in-from-top-2 duration-300">
                  <Input
                    placeholder="Précisez la nature de l'opération..."
                    value={formData.operationTypeOther || ''}
                    onChange={(e) => handleChange('operationTypeOther', e.target.value)}
                    className="focus:border-[#1db584] focus:ring-[#1db584]"
                  />
                </div>
              )}
            </div>

            {/* === TYPE DE CONTENEUR (conditionnel) === */}
            {showContainerOptions && (
              <div className="space-y-3 animate-in slide-in-from-top-2 duration-300 bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#1db584]" />
                  <Label className="text-base font-semibold">Type de conteneur *</Label>
                </div>
                <p className="text-sm text-gray-600 mb-2">
                  Sélectionnez le type de conteneur à transporter
                </p>
                <Select 
                  value={formData.containerType || undefined} 
                  onValueChange={(value) => handleChange('containerType', value)}
                  disabled={!editableFields.vehicule}
                >
                  <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584] bg-white">
                    <SelectValue placeholder="-- Choisissez un type de conteneur --" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px] overflow-y-auto">
                    {containerTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value} className="py-3">
                        <div className="flex items-start gap-2">
                          <span className="text-lg">{type.icon}</span>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.dimensions}</div>
                            <div className="text-xs text-gray-400">{type.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-2 p-3 bg-blue-100 rounded-md">
                  <p className="text-xs text-blue-800 flex items-start gap-2">
                    <span className="text-base">i</span>
                    <span>
                      Les conteneurs sont des unités standardisées pour le transport maritime et routier. 
                      Sélectionnez le type adapté à votre marchandise.
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* === TYPE DE MARCHANDISE (caché si conteneur) === */}
            {!showContainerOptions && (
              <div className={`space-y-2 ${!editableFields.marchandise ? 'opacity-60 pointer-events-none' : ''}`}>
                <Label>Type de marchandise *</Label>
                <Select 
                  value={formData.merchandiseType || undefined} 
                  onValueChange={(value) => handleChange('merchandiseType', value)}
                  disabled={!editableFields.marchandise}
                >
                  <SelectTrigger className="focus:border-[#1db584] focus:ring-[#1db584]">
                    <SelectValue placeholder="-- Sélectionnez --" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    {merchandiseTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.merchandiseType === 'autre' && editableFields.marchandise && (
                  <div className="mt-2 animate-in slide-in-from-top-2 duration-300">
                    <Input
                      placeholder="Précisez le type de marchandise..."
                      value={formData.merchandiseTypeOther || ''}
                      onChange={(e) => handleChange('merchandiseTypeOther', e.target.value)}
                      className="focus:border-[#1db584] focus:ring-[#1db584]"
                    />
                  </div>
                )}
              </div>
            )}

            {/* === POIDS === */}
            <div className={`space-y-2 ${!editableFields.marchandise ? 'opacity-60 pointer-events-none' : ''}`}>
              <Label>Poids approximatif</Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleWeightChange(-1)}
                  disabled={!editableFields.marchandise}
                  className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex-1 flex items-center justify-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="220"
                    value={formData.weight || ''}
                    onChange={(e) => handleWeightInput(e.target.value)}
                    disabled={!editableFields.marchandise}
                    className="w-20 text-2xl font-semibold text-[#1db584] text-center border-b-2 border-gray-200 focus:border-[#1db584] focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
                  />
                  <span className="text-gray-600">tonne{(formData.weight || 1) > 1 ? 's' : ''}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleWeightChange(1)}
                  disabled={!editableFields.marchandise}
                  className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500 text-center">De 1 à 220 tonnes</p>
            </div>

            {/* === NOMBRE DE CAMIONS === */}
            <div className={`space-y-2 ${!editableFields.vehicule ? 'opacity-60 pointer-events-none' : ''}`}>
              <Label>Nombre de camions</Label>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleChange('numberOfTrucks', Math.max(1, (formData.numberOfTrucks || 1) - 1))}
                  disabled={!editableFields.vehicule}
                  className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex-1 flex items-center justify-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={formData.numberOfTrucks || 1}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value, 10);
                      if (val === '' || (!isNaN(val) && val >= 1 && val <= 50)) {
                        handleChange('numberOfTrucks', val === '' ? 1 : val);
                      }
                    }}
                    disabled={!editableFields.vehicule}
                    className="w-16 text-2xl font-semibold text-[#1db584] text-center border-b-2 border-gray-200 focus:border-[#1db584] focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
                  />
                  <span className="text-gray-600">camion{(formData.numberOfTrucks || 1) > 1 ? 's' : ''}</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => handleChange('numberOfTrucks', Math.min(50, (formData.numberOfTrucks || 1) + 1))}
                  disabled={!editableFields.vehicule}
                  className="h-10 w-10 hover:bg-green-50 hover:border-[#1db584] hover:text-[#1db584] transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-gray-500 text-center">De 1 à 50 camions</p>
            </div>

            {/* === PROTECTION & ASSURANCE === */}
            <div className={`space-y-4 border-t pt-4 ${!editableFields.assurance ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[#1db584]" />
                <h3 className="font-semibold text-gray-900">Protection & Assurance</h3>
                {!editableFields.assurance && (
                  <Badge variant="secondary" className="text-xs ml-auto">
                    <Lock className="w-3 h-3 mr-1" />Verrouillé
                  </Badge>
                )}
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="needsInsurance"
                    checked={formData.needsInsurance || false}
                    onChange={(e) => handleChange('needsInsurance', e.target.checked)}
                    disabled={!editableFields.assurance}
                    className="mt-1 h-4 w-4 text-[#1db584] border-gray-300 rounded focus:ring-[#1db584]"
                  />
                  <label htmlFor="needsInsurance" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-medium">Souscrire à une assurance marchandise</span>
                    <p className="text-xs text-gray-500 mt-1">Protection renforcée contre les dommages et pertes durant le transport</p>
                  </label>
                </div>

                {formData.needsInsurance && (
                  <div className="ml-7 space-y-3 animate-in slide-in-from-top-2 duration-300">
                    <div>
                      <Label className="text-sm">Valeur déclarée de la marchandise (XOF)</Label>
                      <Input
                        type="number"
                        placeholder="Ex: 5000000"
                        value={formData.merchandiseValue || ''}
                        onChange={(e) => handleChange('merchandiseValue', e.target.value)}
                        disabled={!editableFields.assurance}
                        className="focus:border-[#1db584] focus:ring-[#1db584] mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Cette valeur servira de base pour le calcul de l&apos;assurance</p>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-sm">Instructions spéciales (optionnel)</Label>
                  <textarea
                    placeholder="Ex: Marchandise fragile, nécessite une manutention délicate..."
                    value={formData.specialInstructions || ''}
                    onChange={(e) => handleChange('specialInstructions', e.target.value)}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:border-[#1db584] focus:ring-[#1db584] focus:outline-none resize-none"
                    rows="3"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h3 className="font-semibold text-lg mb-2">Modification non autorisée</h3>
            <p className="text-gray-600 mb-4">
              Cette opération est au statut "{getStatusLabel(operation.status)}" et ne peut plus être modifiée directement.
            </p>
            <p className="text-sm text-gray-500">
              Contactez notre équipe support si vous avez besoin d'apporter des modifications.
            </p>
          </div>
        )}

        <Separator />

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          {canModify && (
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !hasChanges}
              className="bg-[#1db584] hover:bg-[#19a076]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditOperationModal;
